import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Award } from 'lucide-react';

// ---------------------------------------------------------------------------
// CompareCrossLink
// ---------------------------------------------------------------------------
// SEO cross-link section shown at the bottom of every state / region /
// states-index page, JUST BEFORE the DemoLeadSection. Purpose:
//
//   1. Interlink every regional page to /compare so Google's PageRank
//      flows into the comparison page and helps it rank for competitor-
//      name searches ("Gingr alternative", "Time to Pet vs", etc.).
//   2. Give visitors a natural next-step CTA: "Wait, how does K9os stack
//      up against the app I'm already using?"
//   3. Reinforce competitor names in body copy on every regional page,
//      which further boosts long-tail keyword coverage (e.g. "dog
//      training software Texas Gingr alternative").
//
// The section is intentionally lightweight — a single card, short copy,
// and one primary CTA — so it doesn't compete with the DemoLeadSection
// that follows it.
// ---------------------------------------------------------------------------
export const CompareCrossLink = ({ regionName }) => {
  const location = regionName || 'your area';

  // Top-of-mind competitor names to weave into the copy for the current
  // regional page. Keeping the list to ~5-6 keeps the section scannable
  // and avoids keyword-stuffing penalties.
  const rivals = [
    'Gingr',
    'Revelation Pets',
    'Square',
    'Time to Pet',
    'DoTimely',
    'Kennel Connection',
  ];

  return (
    <section
      className='relative py-14 sm:py-16 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950'
      data-testid='compare-crosslink'
      data-region={regionName || 'unknown'}
    >
      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 md:p-10 shadow-sm'>
          {/* Decorative accent */}
          <div
            aria-hidden='true'
            className='pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#C8102E]/8 dark:bg-[#C8102E]/12 blur-3xl'
          />

          <div className='relative grid gap-6 md:grid-cols-[1fr_auto] md:items-center'>
            <div>
              <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-2'>
                <BarChart3 className='w-3.5 h-3.5' /> Compare &middot; {location}
              </div>
              <h2 className='font-display text-2xl sm:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
                K9os vs {rivals[0]} vs {rivals[1]} vs {rivals[3]}&nbsp;&mdash; and every other top dog training software.
              </h2>
              <p className='mt-3 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed'>
                Trainers in {location} often ask how K9os stacks up against {rivals.slice(0, 5).join(', ')}, and {rivals[5]}. See the full feature-by-feature comparison across scheduling, CRM, POS, memberships, digital contracts, messaging, and marketing &mdash; plus a head-to-head score chart.
              </p>
              {/* Rival "chips" — text-only, no logos, boost keyword surface area */}
              <div className='mt-4 flex flex-wrap gap-2'>
                {rivals.map((r) => (
                  <span
                    key={r}
                    className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                  >
                    K9os vs {r}
                  </span>
                ))}
              </div>
            </div>

            <Link
              to='/compare'
              className='inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap'
              data-testid='compare-crosslink-cta'
            >
              <Award className='w-4 h-4' /> View full comparison
              <ArrowRight className='w-4 h-4' />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompareCrossLink;
